// How long a subscribed module must stay before it can be cancelled.
//
// Launch brands that signed a module on the same day their Launch package went live are locked
// until the contract end date copied from Salesforce. The menu still names the term in months.
// When that date is missing, the lock is the subscribe date plus the term, once. A past end
// date does not get extended. A module added on a later day, a brand that is not on Launch,
// and a missing or unusable term all use the offer's own minimum, once. A zero or negative
// term is treated as missing so a bad Salesforce value cannot unlock cancel early.

const LAUNCH_SEGMENT = 'launch';

// Legacy Launch is offer 31, not its title: admins can rename it, and a rename would quietly
// drop the lock back to 3 months. A variation of 31 is the same package. Offer 40 is not.
const LEGACY_LAUNCH_OFFER_ID = 31;

const positiveInteger = value => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
};

const isLegacyLaunchOffer = offer =>
  offer?.id === LEGACY_LAUNCH_OFFER_ID || offer?.VariationOfSubscriptionOffer_id === LEGACY_LAUNCH_OFFER_ID;

// The calendar day of a stored timestamp. Datetime strings keep their leading date so a value
// that has not been parsed yet matches the UTC day the database stored. Date objects use UTC,
// which is how the database connection reads them.
const calendarDay = value => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

// Missing and unparseable starts sort first (epoch), matching a blank string sorting first.
// Date objects must be compared by timestamp: their text form leads with the weekday name,
// so a later Friday sorts before an earlier Monday.
const startMillis = value => {
  if (!value) return 0;
  const date = value instanceof Date ? value : new Date(value);
  const millis = date.getTime();
  return Number.isFinite(millis) ? millis : 0;
};

const earliestByStart = items => items.slice().sort((a, b) => startMillis(a.startedAt) - startMillis(b.startedAt))[0] || null;

// The Launch package's start, or, for a legacy brand with no package segment, offer 31's start.
// A brand already on another package is not Launch, even if that legacy offer is still active.
const launchAnchorItem = subscriptionItems => {
  const active = (subscriptionItems || []).filter(item => item && !item.isCancelled && item.offer);
  const segmented = active.filter(item => item.offer.segment);
  const packageSegment = segmented.slice().sort((a, b) => (a.offer.id || 0) - (b.offer.id || 0))[0]?.offer.segment || null;

  if (packageSegment === LAUNCH_SEGMENT) {
    const launchItems = segmented.filter(item => item.offer.segment === LAUNCH_SEGMENT);
    const winningOfferId = launchItems.slice().sort((a, b) => (a.offer.id || 0) - (b.offer.id || 0))[0].offer.id;
    return earliestByStart(launchItems.filter(item => item.offer.id === winningOfferId));
  }

  if (packageSegment) return null;

  return earliestByStart(active.filter(item => isLegacyLaunchOffer(item.offer)));
};

// Month-end clamps the way moment's add does: January 31 plus one month is February 28.
const addCalendarMonths = (day, months) => {
  const [year, month, date] = day.split('-').map(Number);
  const monthIndex = month - 1 + months;
  const firstOfTarget = new Date(Date.UTC(year, monthIndex, 1));
  const lastDay = new Date(Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0)).getUTCDate();
  const clamped = new Date(Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth(), Math.min(date, lastDay)));
  return clamped.toISOString().slice(0, 10);
};

const lockedUntilDay = ({ startedAt, termMonths }) => {
  const startDay = calendarDay(startedAt);
  const months = positiveInteger(termMonths);
  if (!startDay || !months) return null;
  return addCalendarMonths(startDay, months);
};

const laterDay = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

const commitmentForItem = ({ offer, startedAt, subscriptionItems, contractLengthMonths, contractEndDate }) => {
  const moduleMinimum = positiveInteger(offer?.minDurationMonths);
  const anchor = launchAnchorItem(subscriptionItems);
  const itemDay = calendarDay(startedAt);
  const anchorDay = anchor ? calendarDay(anchor.startedAt) : null;
  const onContractDay = !!(itemDay && anchorDay && itemDay === anchorDay);
  const contractTerm = onContractDay ? positiveInteger(contractLengthMonths) : null;
  const months = contractTerm || moduleMinimum;
  const minimumDay = lockedUntilDay({ startedAt, termMonths: moduleMinimum });
  // An end date counts only with a whole-number term, and never earlier than the offer minimum.
  const salesforceEnd = contractTerm ? calendarDay(contractEndDate) : null;
  const chosen = salesforceEnd || lockedUntilDay({ startedAt, termMonths: contractTerm }) || minimumDay;

  return {
    months,
    lockedByContract: !!contractTerm,
    lockedUntil: laterDay(chosen, minimumDay)
  };
};

module.exports = {
  commitmentForItem
};
